import {HDate, getPseudoISO} from '@hebcal/hdate';
import {Zmanim} from './zmanim.js';
import {Location} from './location.js';
import {getHolidaysOnDate} from './holidays.js';
import {Event} from './event.js';

function isTomorrowShabbosOrYomTov(dow: number, events: Event[]): boolean {
  return (
    dow === 5 ||
    events.some(ev => ev.hasAnyFlag('LIGHT_CANDLES', 'LIGHT_CANDLES_TZEIS'))
  );
}

/**
 * Returns true if it is _Shabbos_ or if it is a _Yom Tov_ day that has a
 * _melacha_ (work) prohibition.
 *
 * @param dow day of week, where `6` is Shabbos
 * @param events events occurring on the day
 * @return `true` if the day is a _Yom Tov_ that is _assur bemlacha_, or _Shabbos_
 */
function isTodayAssurBemelacha(dow: number, events: Event[]): boolean {
  return dow === 6 || events.some(ev => ev.hasFlag('CHAG'));
}

/**
 * Returns `true` if the given moment (date + time) falls within a period
 * when *melacha* (work) is prohibited — i.e. Shabbat or a Yom Tov.
 *
 * The Shabbat/Yom Tov window is taken to begin at sunset (shkiah) on the
 * preceding day (Erev Shabbat / Erev Yom Tov / Yom Tov sheni) and to end
 * at *tzais* (nightfall) on the day itself. *Tzais* is calculated using a
 * solar depression of 8.5° for simplicity; consult a halachic authority
 * for more stringent opinions.
 *
 * `useElevation` controls whether the location's elevation is taken into
 * account when computing sunset (it has no effect on the degree-based
 * tzais calculation). The Israel/Diaspora schedule comes from
 * `location.getIsrael()`.
 *
 * Throws if sunset cannot be calculated for the given location
 * (e.g. polar regions).
 * @example
 * import {isAssurBemlacha, Location} from '@hebcal/core';
 * const loc = Location.lookup('Jerusalem')!;
 * // Friday after sunset:
 * isAssurBemlacha(new Date('2024-04-26T18:00:00Z'), loc, false); // true
 * @param currentTime the moment to test (with hour/minute)
 * @param location geographic location (also supplies Israel/Diaspora flag and tzid)
 * @param useElevation include elevation when computing sunset
 * @return `true` if *melacha* is prohibited, `false` if it is not
 */
export function isAssurBemlacha(
  currentTime: Date,
  location: Location,
  useElevation: boolean
): boolean {
  // Determine the calendar date in the location's timezone, not the
  // timezone of the computer running this code. Otherwise the day-of-week,
  // Hebrew date and sunset/tzais times would depend on the machine's
  // local timezone, giving different (incorrect) results near midnight.
  const isoDate = getPseudoISO(location.getTzid(), currentTime);
  const year = parseInt(isoDate.substring(0, 4), 10);
  const month = parseInt(isoDate.substring(5, 7), 10);
  const day = parseInt(isoDate.substring(8, 10), 10);
  const hd = new HDate(new Date(year, month - 1, day));
  const zmanim = new Zmanim(location, hd, useElevation);
  // erev shabbos, YT or YT sheni and after shkiah
  const sunset = zmanim.sunset();
  const sunsetMillis = sunset.getTime();
  if (Number.isNaN(sunsetMillis)) {
    throw new Error('Could not determine sunset');
  }
  // erev shabbos, YT or YT sheni and after shkiah
  const il = location.getIsrael();
  const currentMillis = currentTime.getTime();
  const dow = hd.getDay();
  const events = getHolidaysOnDate(hd, il) ?? [];
  if (isTomorrowShabbosOrYomTov(dow, events) && currentMillis >= sunsetMillis) {
    return true;
  }
  // is shabbos or YT and it is before tzais
  if (isTodayAssurBemelacha(dow, events)) {
    const tzais = zmanim.tzeit();
    const tzaisMillis = tzais.getTime();
    return currentMillis <= tzaisMillis;
  }
  return false;
}
